import React, { useState, useCallback, useEffect } from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import { Loader, Modal, Text, Visibility } from '@gasbuddy/react-components';
import classnames from 'classnames/bind';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faHeart as faHeartFilled } from '@fortawesome/free-solid-svg-icons/faHeart';
import { faHeart as faHeartEmpty } from '@fortawesome/pro-light-svg-icons/faHeart';
import { FavoriteListPropType } from '../../prop-types/favoriteList';
import { noop } from '../../../lib/utils';
import AddStationToFavorites from './AddStationToFavorites';
import DeleteStationFromFavorites from './DeleteStationFromFavorites';
import styles from './FavoriteStationIcon.module.css';

const cx = classnames.bind(styles);

export default function FavoriteStationIcon({
  stationId,
  addStationToFavorites,
  isSaving,
  deleteStationFromFavorites,
  isDeleting,
  fetchFavoriteLists,
  isFetching,
  favoriteLists,
  error,
  verbose,
  isLoggedIn,
  identityHost,
  size,
}) {
  const [shouldFetchFavoriteLists, setShouldFetchFavoriteLists] = useState(false);
  const [favoriteListName, setFavoriteListName] = useState();
  const [hasLists, setHasLists] = useState(false);
  const [favoriteListId, setFavoriteListId] = useState();
  const [isFavorite, setIsFavorite] = useState();
  const [isShowingModal, setIsShowingModal] = useState(false);
  const [isSavingChanges, setIsSavingChanges] = useState(false);

  const handleIconClick = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isLoggedIn) {
      window.location.href = `//${identityHost}/login?return_url=${window.location.href}`;
    } else {
      setIsShowingModal(true);
    }
  }, [isLoggedIn, identityHost]);

  const handleDeleteFromFavorites = useCallback(() => {
    setIsSavingChanges(true);
    if (favoriteListId) {
      deleteStationFromFavorites(favoriteListId, stationId);
    }
  }, [deleteStationFromFavorites, favoriteListId, stationId]);

  const handleAddToFavorites = useCallback((listId, stationIdToFavorite) => {
    setIsSavingChanges(true);
    addStationToFavorites(listId, stationIdToFavorite);
  }, [addStationToFavorites]);

  const handleCloseModal = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsShowingModal(false);
  }, []);

  useEffect(() => {
    setShouldFetchFavoriteLists(isLoggedIn && !favoriteLists && !isFetching && !error);
  }, [isLoggedIn, favoriteLists, isFetching, error]);

  useEffect(() => {
    if (shouldFetchFavoriteLists) {
      fetchFavoriteLists();
      setShouldFetchFavoriteLists(false);
    }
  }, [shouldFetchFavoriteLists, fetchFavoriteLists]);

  useEffect(() => {
    if (favoriteLists) {
      const favoriteListWithStation = favoriteLists.find(list => list.poi_ids.includes(parseInt(stationId, 10)));
      const targetList = favoriteListWithStation;

      setHasLists(!!favoriteLists.length);
      setIsFavorite(!!favoriteListWithStation);
      setFavoriteListId(favoriteListWithStation?.id);
      setFavoriteListName(targetList?.name);
    }
  }, [favoriteLists, stationId, hasLists]);

  useEffect(() => {
    if (isShowingModal && isSavingChanges && !isSaving && !isDeleting && !error) {
      setIsSavingChanges(false);
      setIsShowingModal(false);
    }
  }, [isShowingModal, isSavingChanges, isSaving, isDeleting, error]);

  if (isFetching) {
    return <Loader size={size === '2x' ? 'sm' : 'md'} />;
  }

  let modalContent = isFavorite
    ? (
      <DeleteStationFromFavorites
        favoriteListName={favoriteListName}
        onDeleteFromFavorites={handleDeleteFromFavorites}
        onCancel={handleCloseModal}
        error={error}
      />
    )
    : (
      <AddStationToFavorites
        stationId={stationId}
        favoriteLists={favoriteLists}
        onAddStationToFavorites={handleAddToFavorites}
        onCancel={handleCloseModal}
        error={error}
      />
    );

  if (isSaving || isDeleting) {
    modalContent = <Loader size={size === '2x' ? 'sm' : 'md'} />;
  }

  return (
    <div className={cx('favoriteIconContainer', { verbose })}>
      {isLoggedIn && (
        <Modal
          size="sm"
          content={modalContent}
          forceIsShowing={isShowingModal}
        />
      )}
      {/* when modal disappears after favoriting/unfavoriting station, there is a short delay the loader would appear */}
      {isSaving || isDeleting
        ? <Loader size={size === '2x' ? 'sm' : 'md'} />
        : (
          <FontAwesomeIcon
            icon={isFavorite ? faHeartFilled : faHeartEmpty}
            size={size}
            className={cx('favoriteIcon', { isFavorite })}
            onClick={handleIconClick}
          />
        )
      }
      {verbose && (
        <Visibility mobile={false}>
          {!isSaving && !isDeleting && <br />}
          <Text>
            {isFavorite
              ? (
                <React.Fragment>
                  Added to&nbsp;
                  <Link to="/account/favorite-lists" className={cx('favoritesLink')}>Favorite Stations</Link>
                </React.Fragment>
              )
              : 'Add to Favorite Stations'
            }
          </Text>
        </Visibility>
      )}
    </div>
  );
}

FavoriteStationIcon.propTypes = {
  stationId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  fetchFavoriteLists: PropTypes.func,
  isFetching: PropTypes.bool,
  favoriteLists: PropTypes.arrayOf(FavoriteListPropType),
  addStationToFavorites: PropTypes.func,
  isSaving: PropTypes.bool,
  deleteStationFromFavorites: PropTypes.func,
  isDeleting: PropTypes.bool,
  error: PropTypes.string,
  verbose: PropTypes.bool,
  isLoggedIn: PropTypes.bool,
  identityHost: PropTypes.string,
  size: PropTypes.oneOf(['2x', '3x']),
};

FavoriteStationIcon.defaultProps = {
  fetchFavoriteLists: noop,
  isFetching: false,
  favoriteLists: undefined,
  addStationToFavorites: noop,
  isSaving: false,
  deleteStationFromFavorites: noop,
  isDeleting: false,
  error: undefined,
  verbose: false,
  isLoggedIn: false,
  identityHost: 'iam.gasbuddy.com',
  size: '3x',
};
